import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Modal,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useContext, useEffect, useState } from "react";
import MultipleSelect from "../MultipleSelect";
import CancelIcon from "@mui/icons-material/Cancel";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { AnyKindOfDictionary, isEqual } from "lodash";
import { AppContext } from "../../scripts/context/App";
import axios from "axios";
import toast from "react-hot-toast";
import schema from "./schema";

import STYLES from "./styles";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 1200,
  minHeight: "80vh",
  bgcolor: "#fff",
  borderRadius: "4px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
};

interface IProps {
  data: any;
  setData: (data: any) => void;
  handleClose: () => void;
  addNewUser: (data: any) => void;
  updateUser: (data: any) => void;
}

const ERROR_INPUTS = {
  first_name: false,
  last_name: false,
  email: false,
  mobile: false,
  roles: false,
  permissions: false,
};

export default function AdminModal({
  data,
  setData,
  handleClose: handleCloseTemp,
  addNewUser,
  updateUser,
}: IProps) {
  const { permissions, roles_permissions } = useContext(AppContext);
  const [errors, setErrors] = useState<any>(ERROR_INPUTS);
  const [isCreating, setIsCreating] = useState(false);
  const allPermissions = permissions.reduce((acc: any, curr: any) => {
    acc = [...acc, ...curr.perm];
    return acc;
  }, [] as any[]);

  const INPUT_FIELDS = [
    { key: "first_name", label: "First Name", disabled: false },
    { key: "last_name", label: "Last Name", disabled: false },
    { key: "email", label: "Email", disabled: data.edit },
    { key: "mobile", label: "Mobile", type: "number", disabled: false },
  ];

  const handleChange = (e: any) => {
    setData({ [e.target.name]: e.target.value });
  };

  const submitModal = async () => {
    setErrors(ERROR_INPUTS);

    try {
      await schema.validate(data.data, { abortEarly: false });

      setIsCreating(true);

      try {
        const { data: responseData } = await axios.post("/users/create", {
          ...data.data,
          permissions: data.data.permissions.map((permission: any) => permission.name),
        });

        addNewUser(responseData.data);
        toast.success("Admin created successfully");
        handleClose();
      } catch (error: any) {
        const { errors } = error.response.data;
        if (errors) {
          setErrors(errors)
        } else {
          toast.error(
            "Something went wrong. Please try again or contact support."
          );
        }

        console.log(error.response.data);
      }
      setIsCreating(false);
    } catch (error: any) {
      setErrors(ERROR_INPUTS);

      setErrors(
        error.inner.reduce((acc: any, err: any) => {
          acc[err.path] = [err.message];
          return acc;
        }, {})
      );
    }
  };

  const submitEditAdmin = async () => {
    setErrors(ERROR_INPUTS);

    try {
      setIsCreating(true);

      const { data: responseUpdateData } = await axios.post("/users/update", {
        user_id: data.data.id,
        first_name: data.data.first_name,
        last_name: data.data.last_name,
        mobile: data.data.mobile,
        status:
          typeof data.data.status === "string"
            ? data.data.status.toLowerCase() === "active"
              ? 1
              : 0
            : data.data.status
              ? 1
              : 0,
        roles: data.data.roles.map((role: any) =>
          typeof role === "string" ? role : role.name
        ),
        permissions: data.data.permissions.map((permission: any) =>
          typeof permission === "string" ? permission : permission.name
        ),
      });

      toast.success(responseUpdateData.message);
      updateUser(data.data);
      handleClose();
    } catch (error: any) {
      const { errors: responseErrors } = error.response.data;
      if (responseErrors) {
        setErrors(responseErrors);
      } else {
        toast.error(
          "Something went wrong. Please try again or contact support."
        );
      }

      console.log(error.response.data);
    }

    setIsCreating(false);
  };

  useEffect(() => {
    if (
      isEqual(
        allPermissions.map((permission: any) => permission.name),
        data.data.permissions.map((permission: any) => permission.name)
      )
    ) {
      return setData({
        roles: ["administrator"],
      });
    }

    if (data.data.roles.includes("administrator")) return;

    if (!data.data.permissions.length) {
      setData({ roles: [] });
    }
  }, [data.data.permissions]);

  const handleClose = () => {
    setErrors(ERROR_INPUTS);

    handleCloseTemp();
  }

  return (
    <Modal
      keepMounted
      open={data.status}
      onClose={handleClose}
      aria-labelledby="Admin-Modal"
    >
      <Box sx={style}>
        <Box sx={{ p: 3, borderBottom: "1px solid #d3d3d3" }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{ wordBreak: "break-all" }}
          >
            {data.edit
              ? `Edit “${data.data.name}” Admin`
              : "Add New System User"}
          </Typography>
        </Box>
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flex: 1,
            overflowY: "auto",
            maxHeight: "60vh",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "normal" }}>
            General Information
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {INPUT_FIELDS.map((input: any, index: number) => (
              <TextField
                disabled={input.disabled}
                key={index}
                id={`filled-${input.key}`}
                name={input.key}
                label={input.label}
                variant="standard"
                value={data.data[input.key]}
                onChange={handleChange}
                sx={{ flex: "1 1 40%" }}
                error={Boolean(errors[input.key]?.[0])}
                helperText={
                  errors[input.key]?.[0]
                    ? errors[input.key]?.[0].replace(input.key, input.label)
                    : ""
                }
                inputProps={{
                  type: input.type || "string",
                  ...(input.type === "number" ? {
                    min: 0,

                  } : {})
                }}
              />
            ))}
            <MultipleSelect
              errorMessage={errors.roles?.[0]}
              label="Roles"
              handleChange={({ target }: any) => {
                const newRoles =
                  typeof target.value === "string"
                    ? target.value.split(",")
                    : target.value;

                setData({
                  roles: newRoles,
                  permissions: newRoles.reduce((acc: any, role: string) => {
                    if (role === "administrator") {
                      return acc.concat(
                        allPermissions.map(
                          (permission: any) => ({
                            ...permission,
                            is_custom: false,
                          })
                        )
                      );
                    }

                    const find_roles_permissions = roles_permissions.find((r: any) => r.name === role);
                    if (find_roles_permissions) {
                      return [
                        ...data.data.permissions,
                        ...acc,
                        ...(find_roles_permissions?.permissions.map((permission: any) => ({ ...permission, is_custom: false })) || []),
                      ];
                    }
                    return acc;
                  }, []),
                });
              }}
              value={data.data.roles.map((role: any) =>
                typeof role === "object" ? role.name : role
              )}
              options={[
                ...roles_permissions
                  .filter((role: any) => role.status === "Active")
                  .map((role: any) => ({
                    value: role.name,
                    label: role.name,
                  })),
              ]}
              renderValue={(selected) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.length > 0 &&
                      selected.map((value: any, index) => (
                        <Chip
                          variant="outlined"
                          key={index}
                          label={value}
                          onDelete={() => {
                            const filteredRoles = data.data.roles.filter(
                              (role: any) => typeof role === "string" ? role !== value : role.name !== value
                            );
                            const filteredPermissions = roles_permissions.find(
                              (role: any) => role.name === value
                            )?.permissions || [];

                            setData({
                              roles: filteredRoles,
                              permissions: data.data.permissions.filter(
                                (permission: any) => {
                                  return !filteredPermissions.map((s: any) => s.name).includes(permission.name);
                                }
                              ),
                            });
                          }}
                          clickable
                          deleteIcon={
                            <CancelIcon
                              onMouseDown={(event: any) =>
                                event.stopPropagation()
                              }
                            />
                          }
                        />
                      ))}
                  </Box>
                  {selected.length > 0 && (
                    <IconButton
                      onMouseDown={(event: any) => event.stopPropagation()}
                      onClick={() => {
                        setData({
                          roles: [],
                          permissions: [],
                        });
                      }}
                    >
                      <HighlightOffIcon color="error" />
                    </IconButton>
                  )}
                </Box>
              )}
              sx={{ flex: "1 1 400px" }}
            />
            <FormGroup
              sx={{ maxWidth: "fit-content", justifyContent: "center" }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      typeof data.data.status === "string"
                        ? data.data.status === "Active"
                          ? true
                          : false
                        : data.data.status === true
                    }
                  />
                }
                label={
                  typeof data.data.status === "string"
                    ? data.data.status === "Active"
                      ? "Active"
                      : "Inactive"
                    : data.data.status
                      ? "Active"
                      : "Inactive"
                }
                onChange={(e: any) =>
                  setData({
                    ...data.data,
                    status: e.target?.checked ? "Active" : "Inactive",
                  })
                }
              />
            </FormGroup>
          </Box>

          <Divider sx={{ m: "1rem 0" }} />

          <Typography variant="h6" sx={{ fontWeight: "normal" }}>
            Permissions
          </Typography>
          <Box sx={STYLES.permissions_CONTAINER}>
            {permissions.map((role: any, index: number) => {
              if (role.name === "administrator") return <></>;

              let checkIfSomePermisioIsFromRole: boolean = false;
              if (
                role.perm
                  .map((element: any) => {
                    return (
                      data.data.permissions.filter(
                        (permission: any) => permission.name === element.name
                      )[0] || {}
                    );
                  })
                  .some(
                    (element: any) =>
                      "is_custom" in element && !element.is_custom
                  )
              ) {
                checkIfSomePermisioIsFromRole = true;
              } else {
                checkIfSomePermisioIsFromRole = false;
              }

              return (
                <Box
                  key={role.name + " " + index}
                  sx={STYLES.ROLE_PERMISSIONS_CONTAINER}
                >
                  <FormControlLabel
                    sx={STYLES.ROLE_FORM_CONTROL_LABEL}
                    label={role.name}
                    control={
                      <Checkbox
                        checked={role.perm.every((element: any) => {
                          return data.data.permissions
                            .map((permission: any) => permission.name)
                            .includes(element.name);
                        })}
                        disabled={checkIfSomePermisioIsFromRole}
                        indeterminate={
                          role.perm.some((r: any) =>
                            data.data.permissions
                              .map((permission: any) => permission.name)
                              .includes(r.name)
                          ) &&
                          !role.perm.every((element: any) => {
                            return data.data.permissions
                              .map((permission: any) => permission.name)
                              .includes(element.name);
                          })
                        }
                        onChange={(e) => {
                          const newPermissions =
                            e.target.checked === true
                              ? [
                                ...data.data.permissions,
                                ...(role.perm.map((r: any) => r) || []),
                              ]
                              : data.data.permissions.filter(
                                (permission: any) =>
                                  !role.perm
                                    .map((r: any) => r.name)
                                    .includes(permission.name) ||
                                  permission.name === role.name
                              );

                          setData({
                            permissions: newPermissions,
                          });
                        }}
                      />
                    }
                  />
                  <Box sx={STYLES.PERMISSIONS_LIST_CONTAINER}>
                    {role.perm.map((child: any, index: number) => {
                      const currentPermission = data.data.permissions.find(
                        (permission: any) => permission.name === child.name
                      ) || {};
                      const myPermissionsName = data.data.permissions.map((permission: any) => permission.name);
                      let selectedPermissionsArray: any = [];
                      role.perm.forEach((per: any) => {
                        myPermissionsName.includes(per.name) && selectedPermissionsArray.push(per);
                      })
                      const length = role.perm.filter((rr: any) => rr.name.includes("view")).length > 1 ? 2 : 1
                      const checkChildStartWithView = role.perm.find((r: any) => {
                        return myPermissionsName.includes(r.name);
                      }) && child.name.includes("view") && selectedPermissionsArray.length > length;
                      const disabledCheckbox = ("is_custom" in currentPermission && !currentPermission.is_custom) || checkChildStartWithView;

                      return (
                        <FormControlLabel
                          key={child.name + " " + index}
                          label={child.name}
                          sx={{
                            width: "fit-content",
                          }}
                          control={
                            <Checkbox
                              checked={
                                data.data.permissions
                                  .map((permission: any) => permission.name)
                                  .includes(child.name) ||
                                (!data.data.permissions.find(
                                  (permission: any) =>
                                    permission.name === child.name
                                )?.is_custom &&
                                  data.data.permissions
                                    .map((permission: any) => permission.name)
                                    .includes(child.name))
                              }
                              disabled={disabledCheckbox}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setData({
                                    permissions: [
                                      ...data.data.permissions,
                                      ...(data.data.permissions
                                        .map(
                                          (permission: any) => permission.name
                                        )
                                        .includes(
                                          role.perm.find((rr: any) =>
                                            rr.name.includes("view")
                                          )
                                        )
                                        ? []
                                        : [
                                          ...role.perm.filter((rr: any) =>
                                            rr.name.includes("view")
                                          ),
                                        ]),
                                      child,
                                    ],
                                  });
                                } else {
                                  setData({
                                    permissions: data.data.permissions.filter(
                                      (permission: any) =>
                                        permission.name !== child.name &&
                                        permission.name !== role.name
                                    ),
                                  });
                                }
                              }}
                            />
                          }
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: "1px solid #d3d3d3",
            p: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={data.edit ? submitEditAdmin : submitModal}
            disabled={isCreating}
            endIcon={isCreating && <CircularProgress size={20} />}
          >
            {data.edit ? "Edit" : "Create"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={isCreating}
            onClick={handleClose}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
